import { Alert, Input, Modal, Select, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import VSpace from '../../common/VSpace'
import { uniqueId } from '../../../helpers/uniqueId'
import { extractApiErrorMessage } from '../../../helpers/apiErrorMessage'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { Segment } from '../../../domain/features/featureFlags'
import { useSaveSegmentMutation } from './featureFlagsQueries'
import {
  createSegmentDraft,
  SegmentDraft,
  toSegmentSaveInput,
  validateSegmentDraft,
} from './featureFlagsModel'

type Props = {
  open: boolean
  editing: Segment | null
  onClose: () => void
}

const SegmentFormModal = ({ open, editing, onClose }: Props) => {
  const [draft, setDraft] = useState<SegmentDraft>(() => createSegmentDraft(editing))
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const tenantsQuery = useSidebarTenantsQuery()
  const saveMutation = useSaveSegmentMutation()

  const tenantOptions = useMemo(
    () => toTenantOptions(tenantsQuery.data ?? [], { activeOnly: true, alwaysInclude: draft.tenantIds }),
    [tenantsQuery.data, draft.tenantIds]
  )

  useEffect(() => {
    if (open) {
      setDraft(createSegmentDraft(editing))
      setValidationErrors([])
      saveMutation.reset()
    }
  }, [open, editing])

  const handleSave = () => {
    const errors = validateSegmentDraft(draft)
    setValidationErrors(errors)
    if (errors.length > 0) {
      return
    }
    saveMutation.mutate(
      { id: editing?.id ?? uniqueId(), input: toSegmentSaveInput(draft) },
      { onSuccess: onClose }
    )
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={editing ? 'Save' : 'Create'}
      confirmLoading={saveMutation.isLoading}
      title={editing ? `Edit Segment '${editing.name}'` : 'New Segment'}
      destroyOnHidden
    >
      <VSpace size="middle">
        {validationErrors.length > 0 && (
          <Alert
            type="error"
            message={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            }
          />
        )}
        {saveMutation.isError && (
          <Alert
            type="error"
            message={extractApiErrorMessage(saveMutation.error, 'Failed to save segment')}
          />
        )}

        <div>
          <Typography.Text strong>Name</Typography.Text>
          <Input
            placeholder="e.g. Beta testers"
            value={draft.name}
            onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
          />
        </div>

        <div>
          <Typography.Text strong>Tenants</Typography.Text>
          <Select
            mode="multiple"
            value={draft.tenantIds}
            onChange={(tenantIds: number[]) => setDraft((prev) => ({ ...prev, tenantIds }))}
            options={tenantOptions}
            filterOption={filterTenantOption}
            loading={tenantsQuery.isLoading}
            placeholder="Search tenants by name or id"
            style={{ width: '100%' }}
          />
        </div>
      </VSpace>
    </Modal>
  )
}

export default SegmentFormModal
